import { UseQueryResult } from '@tanstack/react-query';
import { Button, MagellanPopup } from 'components/atoms';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiDownload, PiFilePdf, PiImage } from 'react-icons/pi';
import Popup from 'reactjs-popup';
import { PopupActions } from 'reactjs-popup/dist/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import {
  openPdfDocument,
  renderPdfPagesToContainer,
} from 'utils/pdf/tryUnlockPdf';
import './FilePreviewPopup.scss';

interface IFilePreviewPopupProps {
  fileId?: string;
  fileName: string;
  fileService?: (id: string, enabled?: boolean) => UseQueryResult<Blob, Error>;
  fileUrl?: string;
  trigger: JSX.Element;
  onDownload?: () => void;
  onDownloadSuccess?: () => void;
  /** Candidate passwords to unlock protected PDFs (e.g. PESEL/NIP). */
  pdfPasswords?: Array<string | null | undefined>;
  isPasswordProtected?: boolean;
}

const FilePreviewPopup = ({
  fileId,
  fileName,
  fileService,
  fileUrl,
  trigger,
  onDownload,
  onDownloadSuccess,
  pdfPasswords,
  isPasswordProtected,
}: IFilePreviewPopupProps) => {
  const { t } = useTranslation();
  const popupRef = useRef<PopupActions>(null);
  const previewUrlRef = useRef<string | null>(null);
  const pdfCanvasRef = useRef<HTMLDivElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [fileType, setFileType] = useState<'pdf' | 'image' | null>(null);
  const [isUnlockingPdf, setIsUnlockingPdf] = useState(false);
  const [useCanvasPreview, setUseCanvasPreview] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const isImageUrl = Boolean(fileUrl);
  const fileQuery =
    fileService && fileId ? fileService(fileId, !!fileId && isOpen) : null;

  useEffect(() => {
    if (previewUrlRef.current) {
      window.URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }

    setUseCanvasPreview(false);
    pdfCanvasRef.current?.replaceChildren();

    if (isImageUrl && fileUrl) {
      setPreviewUrl(fileUrl);
      setFileType('image');
      return;
    }

    if (!fileQuery?.data) {
      setPreviewUrl(null);
      setFileType(null);
      return;
    }

    const url = window.URL.createObjectURL(fileQuery.data);
    previewUrlRef.current = url;
    setPreviewUrl(url);

    const extension = fileName.split('.').pop()?.toLowerCase();
    const mimeType = fileQuery.data.type;

    if (extension === 'pdf' || mimeType === 'application/pdf') {
      setFileType('pdf');
    } else if (
      extension === 'png' ||
      extension === 'jpg' ||
      extension === 'jpeg' ||
      mimeType?.startsWith('image/')
    ) {
      setFileType('image');
    }

    return () => {
      if (previewUrlRef.current) {
        window.URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
    };
  }, [fileQuery?.data, fileName, fileUrl, isImageUrl]);

  useEffect(() => {
    let cancelled = false;

    const unlockPdf = async () => {
      if (
        !isOpen ||
        !fileQuery?.data ||
        fileType !== 'pdf' ||
        !isPasswordProtected
      ) {
        return;
      }

      setIsUnlockingPdf(true);

      const pdf = await openPdfDocument(fileQuery.data, pdfPasswords);

      if (cancelled) {
        pdf?.destroy();
        return;
      }

      if (!pdf || !pdfCanvasRef.current) {
        setUseCanvasPreview(false);
        setIsUnlockingPdf(false);
        return;
      }

      try {
        await renderPdfPagesToContainer(pdf, pdfCanvasRef.current);
        if (!cancelled) {
          setUseCanvasPreview(true);
        }
      } catch {
        if (!cancelled) {
          setUseCanvasPreview(false);
        }
      } finally {
        pdf.destroy();
        if (!cancelled) {
          setIsUnlockingPdf(false);
        }
      }
    };

    void unlockPdf();

    return () => {
      cancelled = true;
    };
  }, [isOpen, fileQuery?.data, fileType, isPasswordProtected, pdfPasswords]);

  const handleDownload = async () => {
    try {
      if (onDownload) {
        onDownload();
        popupRef.current?.close();
        return;
      }

      if (isImageUrl && fileUrl) {
        const response = await fetch(fileUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const extension = fileName.split('.').pop()?.toLowerCase() || 'jpg';
        a.download = fileName.includes('.')
          ? fileName
          : `${fileName}.${extension}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        onDownloadSuccess?.();
      } else if (fileQuery?.data) {
        const url = window.URL.createObjectURL(fileQuery.data);
        const a = document.createElement('a');
        a.href = url;
        const hasExtension = fileName.includes('.');
        const extension =
          fileQuery.data.type === 'application/pdf'
            ? 'pdf'
            : fileQuery.data.type.startsWith('image/')
              ? fileQuery.data.type.split('/')[1]
              : 'pdf';
        a.download = hasExtension ? fileName : `${fileName}.${extension}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        onDownloadSuccess?.();
      }
    } catch (error) {
      console.error('Błąd podczas pobierania pliku:', error);
      if (isImageUrl && fileUrl) {
        const a = document.createElement('a');
        a.href = fileUrl;
        a.target = '_blank';
        a.download = fileName.includes('.') ? fileName : `${fileName}.jpg`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        onDownloadSuccess?.();
      }
    }
    popupRef.current?.close();
  };

  const handleClose = () => {
    if (previewUrlRef.current) {
      window.URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
      setPreviewUrl(null);
      setFileType(null);
    }
    setUseCanvasPreview(false);
    pdfCanvasRef.current?.replaceChildren();
  };

  return (
    <Popup
      ref={popupRef}
      modal
      nested
      trigger={trigger}
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => {
        handleClose();
        setIsOpen(false);
      }}>
      <MagellanPopup className='file-preview-popup'>
        <MagellanPopup.Header className='justify-center'>
          {fileType === 'pdf' ? <PiFilePdf /> : <PiImage />}
          {fileName}
        </MagellanPopup.Header>
        <div className='file-preview-container'>
          {!isImageUrl && fileQuery?.isLoading ? (
            <div className='flex items-center justify-center p-12'>
              <div className='opacity-50'>{t('table.isLoading')}</div>
            </div>
          ) : !isImageUrl && fileQuery?.isError ? (
            <div className='flex items-center justify-center p-12'>
              <div className='opacity-50'>{t('toast.not-found')}</div>
            </div>
          ) : isUnlockingPdf ? (
            <div className='flex items-center justify-center p-12'>
              <div className='opacity-50'>{t('table.isLoading')}</div>
            </div>
          ) : previewUrl && fileType === 'image' ? (
            <img
              src={previewUrl}
              alt={fileName}
              className='file-preview-image'
            />
          ) : previewUrl && fileType === 'pdf' ? (
            <>
              <div
                ref={pdfCanvasRef}
                className='file-preview-pdf-canvas'
                style={{ display: useCanvasPreview ? 'block' : 'none' }}
              />
              {!useCanvasPreview && (
                <iframe
                  src={previewUrl}
                  className='file-preview-iframe'
                  title={fileName}
                />
              )}
            </>
          ) : (
            <div className='flex items-center justify-center p-12'>
              <div className='opacity-50'>{t('table.noData')}</div>
            </div>
          )}
        </div>
        <MagellanPopup.BottomButtons>
          <Button isClear onClick={() => popupRef.current?.close()}>
            <Button.Text text={t('button.cancel')} />
          </Button>
          <Button
            variant={BUTTON_VARIANT.submit}
            onClick={handleDownload}
            disabled={
              onDownload
                ? false
                : (!fileQuery?.data && !isImageUrl) ||
                  (!isImageUrl && !!fileQuery?.isLoading)
            }>
            <Button.Icon icon={<PiDownload />} />
            <Button.Text text={t('button.download')} />
          </Button>
        </MagellanPopup.BottomButtons>
      </MagellanPopup>
    </Popup>
  );
};

export default FilePreviewPopup;
